import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { KeyRound, Plus, Eye, EyeOff, Copy, Trash2, ShieldCheck, Fingerprint, Share2, History, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { beginAuthenticate, isWebAuthnSupported } from "@/lib/webauthn";
import { useIdleLock } from "@/hooks/useIdleLock";

import { swallow } from "@/lib/log";
import FavPinButtons from "@/components/FavPinButtons";
import RowActions from "@/components/RowActions";
export default function Passwords() {
  const [items, setItems] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [unlocked, setUnlocked] = useState(false);
  const [vaultToken, setVaultToken] = useState(null);
  const [status, setStatus] = useState({ has_passkey: false, require_passkey_for_vault: false });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", username: "", password: "", url: "", notes: "" });
  const [shareOpen, setShareOpen] = useState(null); // passwordId
  const [historyOpen, setHistoryOpen] = useState(null); // passwordId
  const [history, setHistory] = useState([]);
  const [historyRevealed, setHistoryRevealed] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/passwords"); setItems(data); } catch (e) { swallow("Passwords")(e); }
    try { const s = await api.get("/webauthn/status"); setStatus(s.data); } catch (e) { swallow("Passwords")(e); }
    try { const h = await api.get("/households"); setHouseholds(h.data); } catch (e) { swallow("Passwords")(e); }
  };
  useEffect(() => { load(); }, []);

  // Auto-lock after 5 minutes of inactivity (Batch 3E — Vault auto-lock)
  useIdleLock({
    enabled: unlocked,
    timeoutMs: 5 * 60 * 1000,
    warnBeforeMs: 30 * 1000,
    onWarn: () => toast.warning("Vault will lock in 30 seconds due to inactivity"),
    onLock: () => {
      setUnlocked(false);
      setVaultToken(null);
      setRevealed({});
      toast.info("Vault locked after 5 min of inactivity", { duration: 5000 });
    },
  });

  const openNew = () => { setForm({ label: "", username: "", password: "", url: "", notes: "" }); setOpen(true); };

  // Contextual FAB deep-link: `?new=1` opens the new-password dialog (only when unlocked)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (sp.get("new") === "1" && unlocked) {
      openNew();
      const next = new URLSearchParams(sp);
      next.delete("new");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, unlocked]);

  const save = async () => {
    if (!form.label.trim() || !form.password.trim()) { toast.error("Label and password are required"); return; }
    try {
      const { data: saved } = await api.post("/passwords", form);
      // Optimistic add — surfaces the new password immediately even if the SW
      // returns a pre-write /api/passwords snapshot on the reload below.
      if (saved && saved.id) {
        setItems((prev) => (prev.some((x) => x.id === saved.id) ? prev : [saved, ...prev]));
      }
      setOpen(false);
      toast.success("Password saved (encrypted)");
      load(); // background reconciliation
    } catch { toast.error("Failed to save"); }
  };

  const revealHeaders = () => vaultToken ? { "X-Vault-Token": vaultToken } : {};

  const reveal = async (id) => {
    try {
      const { data } = await api.get(`/passwords/${id}/reveal`, { headers: revealHeaders() });
      setRevealed((s) => ({ ...s, [id]: data.password }));
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Passkey unlock required");
        setUnlocked(false); setVaultToken(null);
      } else { toast.error("Reveal failed"); }
    }
  };
  const hide = (id) => setRevealed((s) => { const n = { ...s }; delete n[id]; return n; });

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  // RowActions renders the delete-with-undo toast; here we just do the API + UI update.
  const del = async (p) => {
    setItems((arr) => arr.filter((it) => it.id !== p.id));
    try { await api.delete(`/passwords/${p.id}`); }
    catch { toast.error("Delete failed — restored"); load(); }
  };

  const unlockWithPasskey = async () => {
    try {
      if (!isWebAuthnSupported()) { toast.error("Passkeys not supported here"); return; }
      const { vault_token } = await beginAuthenticate(api);
      setVaultToken(vault_token);
      setUnlocked(true);
      toast.success("Vault unlocked with passkey");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Passkey failed");
    }
  };

  const shareToHousehold = async (pid, hid) => {
    try {
      await api.post("/households/share", { type: "password", id: pid, household_id: hid });
      toast.success("Shared with household");
      setShareOpen(null);
    } catch { toast.error("Share failed"); }
  };

  const openHistory = async (pid) => {
    setHistoryOpen(pid); setHistory([]); setHistoryRevealed({});
    try { const { data } = await api.get(`/passwords/${pid}/history`); setHistory(data || []); }
    catch { toast.error("Failed to load history"); }
  };

  const revealHistoryEntry = async (pid, hid) => {
    try {
      const { data } = await api.get(`/passwords/${pid}/history/${hid}/reveal`, { headers: revealHeaders() });
      setHistoryRevealed((s) => ({ ...s, [hid]: data.password }));
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Passkey unlock required"); }
      else { toast.error("Reveal failed"); }
    }
  };

  return (
    <>
      <TopBar title="Password Vault" subtitle={status.require_passkey_for_vault ? "Passkey required to reveal" : "Encrypted • unlock to reveal"} back
        right={
          <button onClick={openNew} className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="passwords-add-button">
            <Plus className="h-4 w-4" />
          </button>
        }
      />

      {!unlocked ? (
        <div className="px-4 py-8">
          <div className="rounded-3xl border border-border bg-card p-6 text-center" data-testid="vault-locked-state">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="font-display font-semibold text-lg mt-3">Vault locked</div>
            <div className="text-sm text-muted-foreground mt-1">
              {status.has_passkey ? "Use your passkey to unlock." : "Confirm it's you to view saved passwords."}
            </div>

            {status.has_passkey ? (
              <Button className="mt-4 h-11 px-5" onClick={unlockWithPasskey} data-testid="vault-unlock-passkey">
                <Fingerprint className="h-4 w-4 mr-1.5" /> Unlock with passkey
              </Button>
            ) : (
              <>
                <Button className="mt-4 h-11 px-5" onClick={() => { setUnlocked(true); toast.success("Vault unlocked"); }} data-testid="vault-unlock-button">
                  Unlock vault
                </Button>
                {status.require_passkey_for_vault && (
                  <div className="text-[11px] text-muted-foreground mt-3">Passkey required by settings — enroll one in Profile → Passkey unlock.</div>
                )}
              </>
            )}
            <p className="text-[11px] text-muted-foreground mt-4">Passwords are always stored encrypted with Fernet on the server.</p>
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="vault-autolock-hint">
            <Timer className="h-3 w-3" /> Auto-locks after 5 minutes of inactivity
          </div>
          {items.length === 0 && (
            <EmptyState icon={KeyRound} title="No passwords yet" subtitle="Add one, or say 'Save my WiFi password...' in chat." />
          )}
          {items.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card p-4" data-testid="password-item">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-sm truncate">{p.label}</div>
                  {p.username && <div className="text-xs text-muted-foreground truncate">{p.username}</div>}
                  {p.url && <div className="text-[11px] text-muted-foreground truncate">{p.url}</div>}
                  <div className="mt-2 font-mono text-sm tracking-widest" data-testid="password-value">
                    {revealed[p.id] || "••••••••••••"}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end max-w-[45%]">
                  <FavPinButtons
                    type="password"
                    id={p.id}
                    isFavorite={!!p.is_favorite}
                    pinnedAt={p.pinned_at}
                    size="sm"
                    onChanged={(u) => setItems((arr) => arr.map((x) => x.id === p.id ? { ...x, ...u } : x))}
                    onArchived={(id) => setItems((arr) => arr.filter((x) => x.id !== id))}
                  />
                  {revealed[p.id] ? (
                    <button onClick={() => hide(p.id)} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center" data-testid="password-hide-button" aria-label="Hide password"><EyeOff className="h-4 w-4" /></button>
                  ) : (
                    <button onClick={() => reveal(p.id)} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center" data-testid="password-reveal-button" aria-label="Reveal password"><Eye className="h-4 w-4" /></button>
                  )}
                  <RowActions
                    size="sm"
                    testId={`pw-${p.id}`}
                    deleteLabel="Delete password"
                    onCopy={revealed[p.id] ? () => copy(revealed[p.id]) : null}
                    onShare={households.length > 0 ? () => setShareOpen(p.id) : null}
                    onDelete={() => del(p)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Add password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Service / label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="HDFC NetBanking" data-testid="password-form-label" />
            </div>
            <div>
              <Label className="text-xs">Username or email</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="you@example.com or username" data-testid="password-form-username" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="super-secret" data-testid="password-form-password" />
            </div>
            <div>
              <Label className="text-xs">URL</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="password-form-save">Save encrypted</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareOpen} onOpenChange={(v) => !v && setShareOpen(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Share with household</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {households.length === 0 && <div className="text-sm text-muted-foreground">You don't have any households yet.</div>}
            {households.map((h) => (
              <button key={h.id} onClick={() => shareToHousehold(shareOpen, h.id)} className="w-full text-left rounded-xl border border-border bg-card p-3 hover:bg-[hsl(var(--accent))]" data-testid={`share-with-household-${h.id}`}>
                <div className="text-sm font-medium">{h.name}</div>
                <div className="text-xs text-muted-foreground">{h.members.length} member{h.members.length !== 1 ? "s" : ""}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyOpen} onOpenChange={(v) => !v && setHistoryOpen(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><History className="h-4 w-4" /> Password history</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {history.length === 0 && <div className="text-sm text-muted-foreground">No previous versions. Each time you update this password, the old one is archived here.</div>}
            {history.map((h) => (
              <div key={h.id} className="rounded-xl border border-border bg-card p-3" data-testid="password-history-entry">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-muted-foreground">Archived {new Date(h.archived_at).toLocaleString()}</div>
                  {historyRevealed[h.id] ? (
                    <button onClick={() => copy(historyRevealed[h.id])} className="text-xs text-primary inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Copy</button>
                  ) : (
                    <button onClick={() => revealHistoryEntry(historyOpen, h.id)} className="text-xs text-primary inline-flex items-center gap-1" data-testid="password-history-reveal"><Eye className="h-3 w-3" /> Reveal</button>
                  )}
                </div>
                {h.username_at_time && <div className="text-xs text-muted-foreground mt-1">username: {h.username_at_time}</div>}
                {historyRevealed[h.id] && <div className="font-mono text-sm mt-2 break-all">{historyRevealed[h.id]}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
